import { isPlatformServer } from "@angular/common";
import { ApplicationRef, inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { EnvService } from "@gea-waldviertler-website/shop-lib";
import { ToastService } from "./toast.service";

/**
 * Prüft anhand des vom Backend gelieferten Headers `X-Frontend-Version`, ob eine neuere
 * Frontend-Version deployed wurde als die aktuell laufende (`clientAppVersion`).
 * Falls ja, wird ein Hinweis angezeigt und die Seite neu geladen.
 */
@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  /** Key im sessionStorage, um Endlos-Reloads (z.B. durch Caching) zu verhindern */
  private static readonly RELOAD_MARKER = 'app-reloaded-for-version'

  /** Verhindert mehrfaches Auslösen bei parallelen Requests */
  private triggered = false

  private envService = inject(EnvService)

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(ToastService) private toastService: ToastService,
    private appRef: ApplicationRef,
  ) {}

  check(remoteVersion: string | null): void {
    if (isPlatformServer(this.platformId)) return
    if (this.triggered) return
    if (! remoteVersion) return

    const currentVersion = this.envService.config.clientAppVersion
    if (! currentVersion) return

    if (! this.isNewer(remoteVersion, currentVersion)) return

    this.triggered = true

    // Loop-Schutz: wenn wir für genau diese Version schon einmal neu geladen haben,
    // nichts weiter tun (sonst Endlos-Reload, falls altes JS ausgeliefert wird).
    try {
      if (sessionStorage.getItem(VersionCheckService.RELOAD_MARKER) === remoteVersion) return
      sessionStorage.setItem(VersionCheckService.RELOAD_MARKER, remoteVersion)
    } catch {
      // sessionStorage nicht verfügbar (z.B. Private Mode) -> ohne Loop-Schutz fortfahren
    }

    this.toastService.showInfo("Eine neue Version ist verfügbar. Die Seite wird neu geladen …")
    // Der Interceptor-Callback (HTTP-Response) stößt nicht zuverlässig eine
    // Change-Detection-Runde an -> Toast explizit rendern lassen, bevor neu geladen wird.
    Promise.resolve().then(() => this.appRef.tick())
    setTimeout(() => this.reloadPage(), 3000)
  }

  /** Ausgelagert, damit der Reload testbar ist (window.location.reload ist in jsdom nicht ersetzbar). */
  private reloadPage(): void {
    window.location.reload()
  }

  /** Numerischer semver-Vergleich (MAJOR.MINOR.PATCH). Gibt true zurück, wenn a > b. */
  private isNewer(a: string, b: string): boolean {
    const pa = a.split('.')
    const pb = b.split('.')
    const len = Math.max(pa.length, pb.length)

    for (let i = 0; i < len; i++) {
      const na = parseInt(pa[i], 10) || 0
      const nb = parseInt(pb[i], 10) || 0
      if (na > nb) return true
      if (na < nb) return false
    }

    return false
  }
}
